import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import DropdownPopover from "../overlays/DropdownPopover";
import { classNames } from "../styles/classNames";
import {
  FORM_ERROR_TEXT_CLASS,
  FORM_HELP_TEXT_CLASS,
  FORM_LABEL_WITH_SPACING_CLASS,
  FORM_SELECT_CLASS,
} from "../styles/formStyles";

export interface VersionOption {
  value: string;
  label: string;
}

export interface VersionSelectorAction {
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  onSelect: () => void;
}

interface VersionSelectorProps {
  id: string;
  label: string;
  value: string;
  options: VersionOption[];
  onChange: (nextValue: string) => void;
  placeholder: string;
  disabled?: boolean;
  loadingText?: string;
  errorText?: string | null;
  hintText?: string;
  actions?: VersionSelectorAction[];
}

export default function VersionSelector({
  id,
  label,
  value,
  options,
  onChange,
  placeholder,
  disabled = false,
  loadingText,
  errorText,
  hintText,
  actions = [],
}: VersionSelectorProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  );
  const hasActions = actions.length > 0;

  useEffect(() => {
    if (!hasActions) {
      return;
    }

    const closeDropdown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", closeDropdown);
    return () => { document.removeEventListener("mousedown", closeDropdown); };
  }, [hasActions]);

  if (hasActions) {
    return (
      <div className="app-dropdown-anchor" ref={containerRef}>
        <label id={`${id}-label`} htmlFor={id} className={FORM_LABEL_WITH_SPACING_CLASS}>
          {label}
        </label>
        <button
          id={id}
          type="button"
          className={classNames(
            FORM_SELECT_CLASS,
            "relative flex min-h-[2.5rem] items-center text-left"
          )}
          onClick={() => { setOpen((previous) => !previous); }}
          disabled={disabled && options.length === 0}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby={`${id}-label ${id}`}
          title={selectedOption?.label || placeholder}
        >
          <span className={classNames("block min-w-0 truncate", !selectedOption && "app-text-muted")}>
            {selectedOption?.label || placeholder}
          </span>
          <ChevronDownIcon
            aria-hidden="true"
            className={classNames(
              "app-text-muted pointer-events-none absolute inset-y-0 right-2 my-auto h-5 w-5 transition-transform",
              open && "rotate-180"
            )}
          />
        </button>

        <DropdownPopover open={open} align="left" width="full">
          <div className="flex max-h-[min(28rem,calc(100vh-5rem))] flex-col py-1">
            <div className="min-h-0 overflow-y-auto overscroll-contain" role="listbox">
              {options.length > 0 ? (
                options.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={classNames(
                      "flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition",
                      option.value === value
                        ? "app-surface-muted app-text-accent"
                        : "app-text hover:opacity-80"
                    )}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                    role="option"
                    aria-selected={option.value === value}
                  >
                    <span className="min-w-0 truncate">{option.label}</span>
                  </button>
                ))
              ) : (
                <p className="app-text-muted px-3 py-2 text-sm">{placeholder}</p>
              )}
            </div>

            <div className="app-divider-top mt-1 pt-1">
              {actions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  className="app-text flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={action.disabled}
                  onClick={() => {
                    setOpen(false);
                    action.onSelect();
                  }}
                >
                  {action.icon}
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        </DropdownPopover>

        {loadingText ? <p className={FORM_HELP_TEXT_CLASS}>{loadingText}</p> : null}
        {errorText ? <p className={FORM_ERROR_TEXT_CLASS}>{errorText}</p> : null}
        {hintText ? <p className={FORM_HELP_TEXT_CLASS}>{hintText}</p> : null}
      </div>
    );
  }

  return (
    <div>
      <label htmlFor={id} className={FORM_LABEL_WITH_SPACING_CLASS}>
        {label}
      </label>
      <div className="relative">
        <select
          id={id}
          value={value}
          onChange={(event) => { onChange(event.target.value); }}
          disabled={disabled}
          className={FORM_SELECT_CLASS}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDownIcon
          aria-hidden="true"
          className="app-text-muted pointer-events-none absolute inset-y-0 right-2 my-auto h-5 w-5"
        />
      </div>
      {loadingText ? <p className={FORM_HELP_TEXT_CLASS}>{loadingText}</p> : null}
      {errorText ? <p className={FORM_ERROR_TEXT_CLASS}>{errorText}</p> : null}
      {hintText ? <p className={FORM_HELP_TEXT_CLASS}>{hintText}</p> : null}
    </div>
  );
}
